import { loadConfig } from './config.js';
import { getAccessToken } from './auth.js';
import { resolveActiveOrg } from './active-org.js';
import { ApiError } from './errors.js';

/** Cursor pagination meta on list responses (e.g. GET /v1/tokens, #669). */
export interface ListMeta {
  has_more?: boolean;
  cursor?: string;
}

export class ApiClient {
  readonly baseUrl: string;
  private _tokenPromise: Promise<string | null> | undefined;

  constructor() {
    const config = loadConfig();
    this.baseUrl = config.api_url;
  }

  private getToken(): Promise<string | null> {
    if (!this._tokenPromise) this._tokenPromise = getAccessToken();
    return this._tokenPromise;
  }

  private async authHeaders(): Promise<Record<string, string>> {
    const token = await this.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  async request<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
    return (await this.requestEnvelope<T>(method, path, body)).data;
  }

  /**
   * Like `request`, but returns the full `{ data, meta }` envelope so callers can page
   * through cursor-based list endpoints (e.g. `GET /v1/tokens`, #669). `request` unwraps
   * to `.data`; this preserves `meta.has_more`/`meta.cursor`.
   */
  async requestEnvelope<T = unknown>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<{ data: T; meta?: ListMeta }> {
    // Active-org selection (human/JWT only — backend ignores it for rec_ tokens, whose org is
    // bound into the token, and validates membership before honoring it).
    const activeOrg = resolveActiveOrg().orgId;
    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers: {
        ...(await this.authHeaders()),
        'Content-Type': 'application/json',
        'X-Rekor-Client': 'cli',
        ...(activeOrg ? { 'X-Rekor-Active-Org': activeOrg } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    // Read the body once as text — error/204 responses may be empty or non-JSON, and
    // an unconditional res.json() would throw before we can map the status to an ApiError.
    const text = await res.text();
    let parsed: { data?: T; meta?: ListMeta; error?: { message?: string; code?: string } } | undefined;
    if (text) {
      try {
        parsed = JSON.parse(text) as typeof parsed;
      } catch {
        parsed = undefined;
      }
    }

    if (!res.ok) {
      throw new ApiError(
        res.status,
        parsed?.error?.message ?? `HTTP ${res.status}`,
        parsed?.error?.code,
        parsed,
        this.baseUrl,
      );
    }
    return { data: parsed?.data as T, meta: parsed?.meta };
  }

  async uploadFile(url: string, body: BodyInit, contentType: string, extraHeaders?: Record<string, string>): Promise<unknown> {
    const activeOrg = resolveActiveOrg().orgId;
    const res = await fetch(url, {
      method: 'PUT',
      headers: {
        ...(await this.authHeaders()),
        'Content-Type': contentType,
        'X-Rekor-Client': 'cli',
        ...(activeOrg ? { 'X-Rekor-Active-Org': activeOrg } : {}),
        ...extraHeaders,
      },
      body,
    });
    const text = await res.text();
    let parsed: { data?: unknown; error?: { message?: string; code?: string } } | undefined;
    if (text) { try { parsed = JSON.parse(text) as typeof parsed; } catch { parsed = undefined; } }
    if (!res.ok) {
      throw new ApiError(res.status, parsed?.error?.message ?? `Upload failed: HTTP ${res.status}`, parsed?.error?.code, parsed, this.baseUrl);
    }
    return parsed?.data;
  }

  /** Authenticated raw GET that returns the response bytes + content-type (for File downloads). */
  async downloadFile(path: string): Promise<{ buffer: ArrayBuffer; contentType: string }> {
    const activeOrg = resolveActiveOrg().orgId;
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: 'GET',
      headers: {
        ...(await this.authHeaders()),
        'X-Rekor-Client': 'cli',
        ...(activeOrg ? { 'X-Rekor-Active-Org': activeOrg } : {}),
      },
    });
    if (!res.ok) {
      throw new ApiError(res.status, `Download failed: HTTP ${res.status}`, undefined, undefined, this.baseUrl);
    }
    return { buffer: await res.arrayBuffer(), contentType: res.headers.get('Content-Type') ?? 'application/octet-stream' };
  }
}
